import { STORAGE_CHANNEL_KEY } from "~/utils/constants";

const DEFAULT_TOKEN_STORAGE_KEY = "token";
const SOCIAL_LOGIN_BASE_URL = {
	sandbox: "https://sandbox-tw-cs.seeronnet.com",
	production: "https://www.seerq.io",
};
const DEFAULT_OPEN_ID = "";
const DEFAULT_CALLBACK_PARAMS_TO_CLEAR = [
	"code",
	"login_type",
	"type",
	"state",
	"scope",
	"authuser",
	"prompt",
];

const DEFAULT_AUTH_ENDPOINTS = {
	google: "/hkzxcs/user/google",
	line: "/hkzxcs/user/line",
	facebook: "/v1/user/facebook?is_auth=1",
};

const DEFAULT_PROVIDER_META = {
	google: { type: "google", loginType: "google_third", name: "Google", icon: "" },
	line: { type: "line", loginType: "line", name: "LINE", icon: "" },
	facebook: { type: "facebook", loginType: "facebook", name: "Facebook", icon: "" },
};

const DEFAULT_MESSAGES = {
	loginSuccess: "登入成功",
	loginFailed: "登入失敗，請稍後重試",
	authFailed: "授權失敗",
	bindEmailTip: "請繼續綁定您的信箱",
	missingCode: "缺少授權碼，請重新登入",
	codeExpired: "授權碼已過期或已使用，請重新登入",
	bindSuccess: "綁定成功",
	bindFailed: "綁定失敗",
	bindCancelled: "已取消綁定",
	missingThirdPartyData: "缺少第三方登入資訊",
	unsupportedLoginType: "不支援的登入類型",
	missingAuthUrl: "未取得授權地址",
	codeSent: "驗證碼已發送",
};

const HEADER_LANG_MAP = {
	zh_cn: "zh_CN",
	zh_tw: "zh_TW",
	en_us: "en_US",
};

function isPlainObject(value) {
	return Object.prototype.toString.call(value) === "[object Object]";
}

function isBrowser() {
	return typeof window !== "undefined" && typeof document !== "undefined";
}

function getStorageChannel() {
	if (!isBrowser()) return "";

	try {
		return window.localStorage.getItem(STORAGE_CHANNEL_KEY) || "";
	} catch (error) {
		console.error("get social login channel failed", error);
		return "";
	}
}

function createClientError(message, code, payload) {
	const error = new Error(message || "Request failed");
	error.code = code;
	error.payload = payload;
	return error;
}

function resolveBaseUrl(isSandbox, baseUrl) {
	if (baseUrl) return baseUrl;
	return isSandbox ? SOCIAL_LOGIN_BASE_URL.sandbox : SOCIAL_LOGIN_BASE_URL.production;
}

function buildUrl(baseUrl, path) {
	if (/^https?:\/\//i.test(path)) return path;
	if (!baseUrl) return path;
	return `${baseUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

function appendSearchParam(searchParams, key, value) {
	if (value === undefined) return;
	searchParams.append(key, value === null ? "" : String(value));
}

function toSearchParams(data = {}) {
	const searchParams = new URLSearchParams();
	Object.entries(data).forEach(([key, value]) => appendSearchParam(searchParams, key, value));
	return searchParams;
}

function withQuery(url, data) {
	if (!data || !Object.keys(data).length) return url;

	const targetUrl = new URL(url, isBrowser() ? window.location.origin : "http://localhost");
	toSearchParams(data).forEach((value, key) => targetUrl.searchParams.append(key, value));

	if (isBrowser() && targetUrl.origin === window.location.origin) {
		return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
	}
	return targetUrl.toString();
}

function normalizePayload(payload, skipCodeCheck) {
	if (!isPlainObject(payload)) return payload;

	if (!("code" in payload)) {
		return "data" in payload ? normalizePayload(payload.data, skipCodeCheck) : payload;
	}

	if (skipCodeCheck || payload.code === 1 || payload.code === 200) {
		return payload.data !== undefined ? payload.data : payload;
	}

	throw createClientError(
		String(payload.tips || payload.msg || payload.message || "Request failed"),
		payload.code,
		payload
	);
}

async function defaultRequest(requestOptions, config) {
	const method = (requestOptions.method || "POST").toUpperCase();
	const channel = getStorageChannel();
	const headers = {
		...config.headers,
		...requestOptions.headers,
		...(channel ? { channel } : {}),
	};
	const requestUrl =
		method === "GET" ? withQuery(requestOptions.url, requestOptions.data) : requestOptions.url;
	const fetchOptions = {
		method,
		headers,
		credentials: config.credentials,
	};

	if (method !== "GET" && requestOptions.data) {
		headers["Content-Type"] =
			headers["Content-Type"] || "application/x-www-form-urlencoded;charset=UTF-8";
		fetchOptions.body = toSearchParams(requestOptions.data);
	}

	const response = await fetch(requestUrl, fetchOptions);
	const payload = await response.json().catch(() => {
		throw createClientError("Invalid JSON response", response.status);
	});

	if (!response.ok) {
		throw createClientError(
			payload?.tips || payload?.msg || payload?.message || `HTTP ${response.status}`,
			payload?.code || response.status,
			payload
		);
	}

	return payload;
}

export function createSocialLoginClient(options = {}) {
	if (typeof options?.isSandbox !== "boolean") {
		throw createClientError("createSocialLoginClient requires isSandbox");
	}

	const config = {
		baseUrl: resolveBaseUrl(options.isSandbox, options.baseUrl),
		lang: options.lang || "zh_tw",
		platform: options.platform || 74,
		credentials: options.credentials || "omit",
		openId: options.openId ?? DEFAULT_OPEN_ID,
		storageKey: options.storageKey || DEFAULT_TOKEN_STORAGE_KEY,
		providerOrder: options.providerOrder || ["google", "line", "facebook"],
		authEndpoints: {
			...DEFAULT_AUTH_ENDPOINTS,
			...(options.authEndpoints || {}),
		},
		loginEndpoint: options.loginEndpoint || "/v1/login/app",
		bindCodeEndpoint: options.bindCodeEndpoint || "/v1/login/app_captcha_code",
		headers: {
			Platform: String(options.platform || 74),
			Lang: HEADER_LANG_MAP[options.lang || "zh_tw"] || options.lang || "zh_TW",
			...(options.headers || {}),
		},
		autoPersistToken: options.autoPersistToken !== false,
		autoClearUrl: options.autoClearUrl !== false,
		callbackParamsToClear: options.callbackParamsToClear || DEFAULT_CALLBACK_PARAMS_TO_CLEAR,
		...options,
	};
	const messages = {
		...DEFAULT_MESSAGES,
		...(config.messages || {}),
	};
	let pendingBindContext = null;

	function notify(type, message, detail) {
		config.onNotify?.({ type, message, detail });
	}

	function startLoading(scene) {
		const close = config.onLoadingChange?.(true, scene);
		return typeof close === "function" ? close : () => config.onLoadingChange?.(false, scene);
	}

	async function emitResult(result, loginType) {
		await config.onResult?.(result);

		if (result.status === "success") {
			await config.onSuccess?.(result.data, loginType);
			return;
		}

		if (result.status === "needs_bind_email") {
			await config.onNeedBindEmail?.(result.data);
			return;
		}

		if (result.status === "error") {
			await config.onError?.(result.error);
		}
	}

	async function request(requestOptions) {
		const adapter = config.request || defaultRequest;
		const result = await adapter(requestOptions, config);
		return normalizePayload(result, requestOptions.skipCodeCheck);
	}

	function getProviderMeta(loginType) {
		const customProvider = (config.providers || []).find(item => item.type === loginType) || {};
		const defaultProvider = DEFAULT_PROVIDER_META[loginType] || {};
		return {
			type: customProvider.type || defaultProvider.type || loginType,
			name: customProvider.name || defaultProvider.name || loginType,
			icon: customProvider.icon || defaultProvider.icon || "",
			loginType: customProvider.loginType || defaultProvider.loginType || loginType,
			firebaseEvent: customProvider.firebaseEvent || defaultProvider.firebaseEvent || "",
		};
	}

	function getProviderList(customProviders) {
		const providers = customProviders?.length ? customProviders : config.providers;
		if (providers?.length)
			return providers.map(item => ({ ...getProviderMeta(item.type), ...item }));
		return config.providerOrder.map(type => getProviderMeta(type));
	}

	function isSupportedLoginType(loginType) {
		return Boolean(loginType && config.authEndpoints[loginType]);
	}

	function getCurrentUrl() {
		return isBrowser() ? new URL(window.location.href) : null;
	}

	function getCleanRedirectUrl() {
		const url = getCurrentUrl();
		if (!url) return "";
		config.callbackParamsToClear.forEach(param => url.searchParams.delete(param));
		url.hash = "";
		return url.toString();
	}

	function getCallbackRedirectUrl(loginType) {
		const url = new URL(getCleanRedirectUrl());
		const callbackQuery =
			typeof config.callbackQuery === "function"
				? config.callbackQuery(loginType)
				: config.callbackQuery;
		Object.entries(callbackQuery || {}).forEach(([key, value]) => {
			if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
		});
		url.searchParams.set("login_type", loginType);
		return url.toString();
	}

	function clearThirdPartyUrlParams(extraParams = []) {
		const url = getCurrentUrl();
		if (!url) return;
		[...config.callbackParamsToClear, ...extraParams].forEach(param =>
			url.searchParams.delete(param)
		);
		url.hash = "";
		window.history.replaceState({}, document.title, url.toString());
	}

	function getCallbackParams() {
		if (!isBrowser()) return { code: "", loginType: "" };
		const params = new URLSearchParams(window.location.search);
		return {
			code: params.get("code") || "",
			loginType: params.get("login_type") || params.get("type") || "",
		};
	}

	function resolveLoginTypeForApi(loginType) {
		return config.mapLoginTypeForApi?.(loginType) || getProviderMeta(loginType).loginType;
	}

	function getLoginParams(extraParams = {}) {
		const params =
			typeof config.loginParams === "function" ? config.loginParams() : config.loginParams || {};
		return {
			lang: config.lang,
			...params,
			...extraParams,
		};
	}

	async function persistToken(token, responseData) {
		if (!token) return;
		if (config.persistToken) {
			await config.persistToken(token, responseData);
			return;
		}
		if (config.autoPersistToken && isBrowser()) {
			window.localStorage.setItem(config.storageKey, token);
		}
	}

	async function finishLogin(responseData, loginType) {
		if (!responseData?.token) {
			throw createClientError(
				responseData?.msg || responseData?.tips || messages.loginFailed,
				undefined,
				responseData
			);
		}

		await persistToken(responseData.token, responseData);
		notify("success", messages.loginSuccess, responseData);
		if (config.autoClearUrl) clearThirdPartyUrlParams();

		const result = { status: "success", data: responseData };
		await emitResult(result, loginType);
		return result;
	}

	async function handleError(error, fallbackMessage) {
		const isCodeExpired =
			error?.payload?.error === "invalid_grant" || error?.message === "invalid_grant";
		const message = isCodeExpired ? messages.codeExpired : error?.message || fallbackMessage;
		notify("error", message, error);
		if (config.autoClearUrl) clearThirdPartyUrlParams();
		const result = {
			status: "error",
			error: error instanceof Error ? error : createClientError(message),
		};
		await emitResult(result);
		return result;
	}

	async function getAuthUrl(loginType) {
		if (!isSupportedLoginType(loginType)) {
			notify("error", messages.unsupportedLoginType);
			return "";
		}

		try {
			const redirectUri = getCallbackRedirectUrl(loginType);
			const data = config.buildAuthRequestPayload?.(loginType, redirectUri) || { url: redirectUri };
			const responseData = await request({
				url: buildUrl(config.baseUrl, config.authEndpoints[loginType] || ""),
				method: "GET",
				data,
			});

			if (!responseData?.auth_url)
				throw createClientError(messages.missingAuthUrl, undefined, responseData);
			return responseData.auth_url;
		} catch (error) {
			notify("error", messages.authFailed, error);
			return "";
		}
	}

	async function redirectToProvider(loginType) {
		if (!isBrowser()) return false;

		const authUrl = await getAuthUrl(loginType);
		if (!authUrl) return false;

		window.location.href = authUrl;
		return true;
	}

	async function handleCallback(params = getCallbackParams(), extraLoginParams = {}) {
		const { code, loginType } = params;
		if (!code)
			return await handleError(createClientError(messages.missingCode), messages.missingCode);
		if (!isSupportedLoginType(loginType)) {
			return await handleError(
				createClientError(messages.unsupportedLoginType),
				messages.unsupportedLoginType
			);
		}

		const closeLoading = startLoading("login");
		const apiLoginType = resolveLoginTypeForApi(loginType);

		try {
			const payload = {
				login_type: apiLoginType,
				third_access_token: code,
				redirect_uri: getCallbackRedirectUrl(loginType),
				...getLoginParams(extraLoginParams),
			};
			if (config.openId) payload.open_id = config.openId;

			const finalPayload =
				config.transformLoginPayload?.(payload, { code, loginType, apiLoginType }) || payload;
			const responseData = await request({
				url: buildUrl(config.baseUrl, config.loginEndpoint),
				method: "POST",
				data: finalPayload,
			});

			return await finishLogin(responseData, loginType);
		} catch (error) {
			if (error.code === 1075) {
				pendingBindContext = {
					provider: loginType,
					login_type: apiLoginType,
					third_access_token: code,
				};

				if (config.openId) pendingBindContext.open_id = config.openId;

				notify("info", messages.bindEmailTip, pendingBindContext);
				const result = { status: "needs_bind_email", data: { ...pendingBindContext } };
				await emitResult(result);
				return result;
			}

			return await handleError(error, messages.loginFailed);
		} finally {
			closeLoading();
		}
	}

	async function checkAndHandleCallback(extraLoginParams = {}) {
		const params = getCallbackParams();
		if (!params.code && !params.loginType) {
			const result = { status: "idle" };
			await emitResult(result);
			return result;
		}
		return handleCallback(params, extraLoginParams);
	}

	return {
		baseUrl: config.baseUrl,
		openId: config.openId,
		providers: getProviderList(),
		getProviderList,
		isSupportedLoginType,
		getAuthUrl,
		redirectToProvider,
		thirdPartyLogin: redirectToProvider,
		handleCallback,
		checkAndHandleCallback,
		clearThirdPartyUrlParams,
		getCleanRedirectUrl,
		getCallbackRedirectUrl,
		getPendingBindContext: () => (pendingBindContext ? { ...pendingBindContext } : null),
	};
}

export default createSocialLoginClient;
